import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { Wallet, Receipt, MessageSquare, FileText, AlertCircle, ArrowRight, Home, ShieldAlert } from "lucide-react";
import { TerminosDialog } from "../../components/TerminosDialog";
import { toast } from "sonner";

export default function PortalDashboard() {
  const { token, API, user } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [deuda, setDeuda] = useState({ total_pendiente: 0, total_liquidaciones: 0, inmuebles: [] });
  const [terminos, setTerminos] = useState({ requiere_reaceptacion: false, version_aceptada: null, version_vigente: null });
  const [terminosOpen, setTerminosOpen] = useState(false);
  const [aceptandoTerms, setAceptandoTerms] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/liquidaciones/mi-deuda`, auth).then(r => setDeuda(r.data)).catch(() => {});
    axios.get(`${API}/auth/mis-terminos`, auth).then(r => setTerminos(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const reaceptar = async () => {
    setAceptandoTerms(true);
    try {
      const r = await axios.post(`${API}/auth/aceptar-terminos`, {}, auth);
      setTerminos(t => ({ ...t, version_aceptada: r.data.version, requiere_reaceptacion: false, aceptada_at: r.data.aceptada_at }));
      toast.success(`Términos v${r.data.version} aceptados. ¡Gracias!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo registrar la aceptación");
    } finally { setAceptandoTerms(false); }
  };

  const fmt = n => `Bs. ${(Number(n)||0).toLocaleString("es-VE", { maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900">Bienvenido, {user?.nombre}</h1>
        <p className="text-green-700/70 mt-1">Su portal del contribuyente · {user?.email}</p>
      </div>

      {/* Banner de re-aceptación de Términos (solo si hay nueva versión) */}
      {terminos.requiere_reaceptacion && terminos.version_aceptada && (
        <Card className="p-4 border-2 border-amber-300 bg-amber-50" data-testid="banner-reaceptar-terminos">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-amber-900">Actualizamos nuestros Términos y Condiciones</p>
              <p className="text-sm text-amber-800 mt-1">
                Usted aceptó la versión <b>{terminos.version_aceptada}</b>. La versión vigente es <b>{terminos.version_vigente}</b>. Revise los cambios y confirme para seguir usando el portal.
              </p>
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTerminosOpen(true)}
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                  data-testid="btn-ver-terminos"
                >Ver Términos</Button>
                <Button
                  size="sm"
                  onClick={reaceptar}
                  disabled={aceptandoTerms}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="btn-reaceptar-terminos"
                >{aceptandoTerms ? "Aceptando…" : `Acepto versión ${terminos.version_vigente}`}</Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Hero deuda */}
      <Card className="p-8 border-0 text-white shadow-xl" style={{ background: "linear-gradient(135deg,#14532D 0%,#166534 50%,#EA580C 130%)" }} data-testid="hero-deuda">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-orange-300 text-sm font-bold uppercase tracking-wider">Su deuda actual</p>
            <p className="font-display font-extrabold text-4xl md:text-5xl mt-2">{fmt(deuda.total_pendiente)}</p>
            <p className="text-white/80 text-sm mt-1">{deuda.total_liquidaciones} liquidación(es) pendiente(s)</p>
          </div>
          {deuda.total_pendiente > 0 && (
            <Button data-testid="btn-pagar-ahora" onClick={() => navigate("/portal/reportar-pago")} size="lg" className="bg-white text-green-900 hover:bg-orange-50 font-semibold shadow-lg">
              Pagar ahora <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </Card>

      {/* Accesos rápidos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: AlertCircle, label: "Ver mi deuda", path: "/portal/deuda", accent: "text-orange-600" },
          { icon: Receipt, label: "Reportar pago", path: "/portal/reportar-pago", accent: "text-green-700" },
          { icon: FileText, label: "Historial / Recibos", path: "/portal/historial", accent: "text-emerald-700" },
          { icon: MessageSquare, label: "Mis reclamos", path: "/portal/reclamos", accent: "text-blue-700" },
        ].map((q) => {
          const Icon = q.icon;
          return (
            <Card key={q.path} onClick={() => navigate(q.path)} className="kpi-card p-5 border-green-100 cursor-pointer hover:border-orange-300" data-testid={`quick-${q.path.split("/").pop()}`}>
              <div className={`w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center mb-3 ${q.accent}`}>
                <Icon className="w-6 h-6" />
              </div>
              <p className="font-display font-bold text-green-900 text-base">{q.label}</p>
              <p className="text-xs text-green-700/60 mt-1">Ingresar →</p>
            </Card>
          );
        })}
      </div>

      {/* Inmuebles */}
      {deuda.inmuebles && deuda.inmuebles.length > 0 && (
        <Card className="p-5 border-green-100">
          <div className="flex items-center gap-2 mb-4">
            <Home className="w-5 h-5 text-green-700" />
            <h3 className="font-display font-bold text-lg text-green-900">Mis Inmuebles</h3>
          </div>
          <div className="space-y-3">
            {deuda.inmuebles.map((b, idx) => (
              <div key={idx} className="p-4 bg-green-50/40 rounded-lg border border-green-100 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <p className="font-medium text-green-900">{b.inmueble.direccion}</p>
                  <p className="text-xs text-green-700/70">{b.inmueble.tipo_zona} · {b.inmueble.metros_cuadrados} m² · {b.liquidaciones_pendientes.length} pendiente(s)</p>
                </div>
                <p className="font-display font-bold text-lg text-orange-600">{fmt(b.subtotal)}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Diálogo de Términos y Condiciones */}
      <TerminosDialog open={terminosOpen} onOpenChange={setTerminosOpen} />
    </div>
  );
}
